/**
 * Mover catalog (3D-canonical).
 *
 * Magnet-plate-center-Mm is the canonical attachment point on the path
 * centerline (after originCorrection of the GLB).
 */

import type { MoverType3D, Vec3 } from '../types.js';

export interface MoverCatalogEntry {
  moverType: MoverType3D;
  /** Mover length in travel direction, mm. */
  pathLengthMm: number;
  /** Mover height in CAD Y-direction, mm (front side / encoder). */
  heightMm: number;
  /**
   * Magnet plate center in mover-local 3D frame (after originCorrection).
   * +X = travel direction, +Z = up, Y = left of path.
   */
  magnetPlateCenterMm: Vec3;
}

export const MOVER_CATALOG: Readonly<Record<MoverType3D, MoverCatalogEntry>> =
  Object.freeze({
    AT9011_0050: {
      moverType: 'AT9011_0050',
      pathLengthMm: 50,
      heightMm: 123.5,
      magnetPlateCenterMm: [0, 0, 0],
    },
    AT9011_0070: {
      moverType: 'AT9011_0070',
      pathLengthMm: 70,
      heightMm: 126,
      magnetPlateCenterMm: [0, 0, 0],
    },
    AT9012_0050: {
      moverType: 'AT9012_0050',
      pathLengthMm: 51,
      heightMm: 123.5,
      magnetPlateCenterMm: [0, 0, 0],
    },
    AT9014_0055: {
      moverType: 'AT9014_0055',
      pathLengthMm: 55,
      heightMm: 124.8,
      magnetPlateCenterMm: [0, 0, 0],
    },
    AT9014_0070: {
      moverType: 'AT9014_0070',
      pathLengthMm: 70,
      heightMm: 125.8,
      magnetPlateCenterMm: [0, 0, 0],
    },
    ATH9011_0075: {
      moverType: 'ATH9011_0075',
      pathLengthMm: 75,
      heightMm: 112,
      magnetPlateCenterMm: [0, 0, 0],
    },
    ATH9013_0075: {
      moverType: 'ATH9013_0075',
      pathLengthMm: 75,
      heightMm: 112,
      magnetPlateCenterMm: [0, 0, 0],
    },
    Custom: {
      moverType: 'Custom',
      pathLengthMm: 70,
      heightMm: 125,
      magnetPlateCenterMm: [0, 0, 0],
    },
    // Hepco GFX2-1TC-S25: AABB 83.2 × 85.5 × 106.4 mm in CAD frame
    // (X = lateral, Y = up, Z = path direction). Defaults below match the
    // CAD path-length; calibration tool refines positioning.
    Hepco_GFX2_1TC_S25: {
      moverType: 'Hepco_GFX2_1TC_S25',
      pathLengthMm: 100,
      heightMm: 86,
      magnetPlateCenterMm: [0, 0, 0],
    },
  });

export function getMoverEntry(
  moverType: MoverType3D,
): MoverCatalogEntry | undefined {
  return MOVER_CATALOG[moverType];
}

export function isKnownMoverType(value: string): value is MoverType3D {
  return Object.prototype.hasOwnProperty.call(MOVER_CATALOG, value);
}
